import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

/**
 * The frame every record screen shares: a header that says what this record is,
 * a tab strip, and the content region under it.
 *
 * It exists so the student, cohort, enrolment and campaign screens PHASES 08–12
 * add do not each re-invent "where does the status pill go" — and so the answer
 * can be changed once. Tabs are LINKS, not local state: which tab you are on is
 * part of the address, so a colleague can be sent to the attendance tab of a
 * cohort rather than to the cohort with instructions to click.
 *
 * The header is sticky at the top of the scroll region. On a long form, losing
 * sight of which record you are editing is how the wrong record gets edited.
 */

export interface DetailTab {
  id: string;
  label: string;
  to: string;
  /** A count, a warning dot, anything short. */
  badge?: ReactNode;
  disabled?: boolean;
}

export interface DetailShellProps {
  /** The record's name. Rendered as the page's `h1`. */
  title: string;
  /** Small mono line above the title — a record id, a collection name. */
  eyebrow?: ReactNode;
  /** One line under the title: what this record is, not a repeat of the title. */
  subtitle?: ReactNode;
  /** Status pills, a tier badge — anything that qualifies the title. */
  meta?: ReactNode;
  /** Primary and secondary actions, top-end. */
  actions?: ReactNode;
  /** Where "back" goes. Omit on a screen reached from nowhere in particular. */
  backTo?: { to: string; label: string };
  tabs?: DetailTab[];
  /** Which tab is current. Compared against `DetailTab.id`. */
  activeTab?: string;
  children: ReactNode;
  className?: string;
}

export function DetailShell({
  title,
  eyebrow,
  subtitle,
  meta,
  actions,
  backTo,
  tabs,
  activeTab,
  children,
  className,
}: DetailShellProps) {
  const { t } = useTranslation();
  return (
    <div className={cn('flex flex-col', className)}>
      <header className="sticky top-0 z-20 -mx-md border-b-[0.5px] border-hairline bg-canvas-sand/95 px-md pt-md backdrop-blur-xs md:-mx-lg md:px-lg lg:-mx-xl lg:px-xl">
        {backTo && (
          <Link
            to={backTo.to}
            className="inline-flex items-center gap-2xs rounded-xs text-small text-accent-blue transition-colors duration-almanac hover:text-ink-deep focus-visible:shadow-focus"
          >
            <span aria-hidden>←</span>
            {t('ui.backTo', { label: backTo.label })}
          </Link>
        )}

        <div className="flex flex-wrap items-start justify-between gap-sm pt-2xs">
          <div className="min-w-0 flex-1 basis-64">
            {eyebrow && <p className="eyebrow text-ink-mute">{eyebrow}</p>}
            {/* `min-w-0` again here, not only on the column above: a flex item's
                default `min-width:auto` is its MIN-CONTENT width, and for an
                unbroken token — a pasted URL, an email used as a name, a
                run-together transliteration — that is the whole rendered width
                of the token. The outer min-w-0 cannot help, because this row
                re-establishes the constraint one level down; a 200-character
                name (the server's ceiling, B-22) took the document to 3,587px
                on a 2,560px window and pushed Edit, Erase and the tab strip off
                screen (F-25). `wrap-break-word` then lets the token wrap rather
                than merely clip, so the whole name stays readable. */}
            <div className="flex min-w-0 flex-wrap items-center gap-sm">
              <h1 className="min-w-0 wrap-break-word text-title text-ink-deep">{title}</h1>
              {meta}
            </div>
            {subtitle && <p className="mt-2xs wrap-break-word text-small text-ink-mid">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-xs">{actions}</div>}
        </div>

        {tabs && tabs.length > 0 && (
          <nav aria-label={t('ui.recordSections')} className="mt-md">
            {/* Horizontally scrollable rather than wrapped: a wrapping tab strip
                changes the header's height as the window narrows, which moves
                the content under a sticky header at the worst moment. */}
            <ul className="-mb-px flex gap-md overflow-x-auto">
              {tabs.map((tab) => {
                const current = tab.id === activeTab;
                return (
                  <li key={tab.id} className="shrink-0">
                    {tab.disabled ? (
                      <span
                        aria-disabled
                        className="inline-flex items-center gap-2xs border-b-2 border-transparent pb-sm text-small text-ink-mute opacity-50"
                      >
                        {tab.label}
                        {tab.badge}
                      </span>
                    ) : (
                      <Link
                        to={tab.to}
                        aria-current={current ? 'page' : undefined}
                        className={cn(
                          'inline-flex items-center gap-2xs border-b-2 pb-sm text-small',
                          'transition-colors duration-almanac focus-visible:shadow-focus',
                          current
                            ? 'border-accent-blue font-medium text-accent-blue'
                            : 'border-transparent text-ink-mid hover:border-hairline-strong hover:text-ink-deep',
                        )}
                      >
                        {tab.label}
                        {tab.badge}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </header>

      <div className="pt-lg">{children}</div>
    </div>
  );
}
